//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { IHttpService, IPromise } from 'angular';
import {
    IPromotedPostsMetricsResponse,
    IPromotedPostsStatusUpdateResponse,
    IPromotedPostSummaryMetricsResponse,
    IUpdateAdInsightsForPostIdResponse,
} from 'app/promoted-posts-metrics/models/promoted-posts-metrics-request-types';
import {
    PromotedPostsMetricsStatuses,
} from 'app/promoted-posts-metrics/promoted-posts-metrics-constants/promoted-posts-metrics-constants';
import promotedPostsMetricsModule from 'app/promoted-posts-metrics/promoted-posts-metrics-module';
import {
    PROMOTED_POSTS_METRICS_API_SORT_KEY,
} from 'app/promoted-posts-metrics/promoted-posts-metrics-table/promoted-posts-metrics-table-values';

export default class PromotedPostsMetricsGatewayApiService {
  private BASE_URL;

  constructor(
        private $http: IHttpService,
        private PromiseService,
        private SessionVarsService,
    ) {
    const webGatewayUrl = this.SessionVarsService.getBaseUrl('web_gateway');
    this.BASE_URL = `${webGatewayUrl}/api/v1/promoted-posts`;
  }

    /**
     * Gets details about the client's FB and IG account settings. Needed
     * to determine which onboarding steps have been completed.
     *
     * /api/v1/promoted-posts/onboarding-state
     *
     * @returns A collection of values used to determine which onboarding
     * steps are complete
     */
  public fetchOnboardingState() {
    return this.PromiseService.processHttpResponse(
            this.$http.get(`${this.BASE_URL}/onboarding-state`),
        );
  }

  public getPromotedPostsMetricsSummary(
        startDate: string,
        endDate: string,
    ): IPromise<IPromotedPostSummaryMetricsResponse> {
    const params = {
      startDate,
      endDate,
    };

    return this.PromiseService.processHttpResponse(
            this.$http.get(`${this.BASE_URL}/summary-metrics`, { params }),
        );
  }

  public getPromotedPostsMetricsForRange(
        startDate: string,
        endDate: string,
        timezone: string,
        limit: number,
        offset: number,
        sortKey: string,
        sortDirection: string,
    ): IPromise<IPromotedPostsMetricsResponse> {
    const params = {
      startDate,
      endDate,
      timezone,
      limit,
      offset,
      sortKey: PROMOTED_POSTS_METRICS_API_SORT_KEY[sortKey],
      sortDirection,
    };

    return this.PromiseService.processHttpResponse(
            this.$http.get(`${this.BASE_URL}/posts-metrics-range`, { params }),
        );
  }

  public getRecentPromotionsForClientAsync(limit: number): any {
    const params = {
      limit,
    };

    return this.PromiseService.processHttpResponse(
            this.$http.get(`${this.BASE_URL}/posts-metrics-recent`, { params }),
        );
  }

  public pausePromotion(campaignId: string, adAccountId: string): IPromise<IPromotedPostsStatusUpdateResponse> {
    const params = {
      campaignId,
      adAccountId,
    };

    return this.PromiseService.processHttpResponse(
            this.$http.put(`${this.BASE_URL}/pause-promotion`, {}, { params }),
        ).then(() => {
            // As of DEV-39078, the response is empty and we must rely on whether the call succeeded or failed
          return { status: PromotedPostsMetricsStatuses.PAUSED };
        }, () => {
          return { status: PromotedPostsMetricsStatuses.ERROR };
        });
  }

  public resumePromotion(campaignId: string, adAccountId: string): IPromise<IPromotedPostsStatusUpdateResponse> {
    const params = {
      campaignId,
      adAccountId,
    };

    return this.PromiseService.processHttpResponse(
            this.$http.put(`${this.BASE_URL}/resume-promotion`, {}, { params }),
        ).then(() => {
            // As of DEV-39078, the response is empty and we must rely on whether the call succeeded or failed
          return { status: PromotedPostsMetricsStatuses.RUNNING };
        }, () => {
          return { status: PromotedPostsMetricsStatuses.ERROR };
        });
  }

  public deletePromotion(campaignId: string, adAccountId: string): IPromise<IPromotedPostsStatusUpdateResponse> {
    const params = {
      campaignId,
      adAccountId,
    };

    return this.PromiseService.processHttpResponse(
            this.$http.put(`${this.BASE_URL}/delete-promotion`, {}, { params }),
        ).then(() => {
            // As of DEV-39078, the response is empty and we must rely on whether the call succeeded or failed
          return { status: PromotedPostsMetricsStatuses.DELETED };
        }, () => {
          return { status: PromotedPostsMetricsStatuses.ERROR };
        });
  }

  public updateAdInsightsForPostId(
        postId: string,
        desiredStatus: string,
    ): IPromise<IUpdateAdInsightsForPostIdResponse> {
    const params = {
      postId,
    };

    return this.PromiseService.processHttpResponse(
            this.$http.get(`${this.BASE_URL}/update-insights`, { params }),
        ).then(() => {
          return {
            postId,
            desiredStatus,
          };
        }, () => {
          return {
            postId,
            status: PromotedPostsMetricsStatuses.ERROR,
          };
        });
  }

  public getCurrentlyRunningAds(): IPromise<IPromotedPostsMetricsResponse> {
    return this.PromiseService.processHttpResponse(
            this.$http.get(`${this.BASE_URL}/posts-metrics-running`),
        ).then((response) => {
          return response;
        }, () => {
          return {
            posts: [],
            total: 0,
          };
        });
  }

  public generatePromotedPostExportUrl() {
    return `${this.BASE_URL}/posts.csv`;
  }
}

promotedPostsMetricsModule.service('PromotedPostsMetricsGatewayApiService', [
  '$http',
  'PromiseService',
  'SessionVarsService',
  PromotedPostsMetricsGatewayApiService,
]);
